import { DateTime } from '@ksyos/date-time';
import {
    Button,
    Card,
    Form,
    FormButtonRow,
    FormGroup,
    ProcessItemContainer,
    ProcessItemHeader,
} from '@ksyos/design-system';
import { TextAreaFormGroupField } from '@frontend/components/fields/TextAreaField';
import { scrollValidationErrorIntoCenterViewInScrollContainer } from '@frontend/utils';
import type { ValidationErrors } from '../../../utils/validation';
import type { IPatientContact } from '../../interfaces/patientContact';
import { ServiceType } from '../../../../common/enums/serviceType';
import { getDisplayName } from '../../../../common/utils/getDisplayName';
import { patientContactSchema, patientContactUpdateSchema } from '../../../../common/validation/patientContact';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../utils/validation';
import { Component } from '../../elements/Component';
import { FormContextProvider } from '../../elements/formContext';
import { DateInput } from '../../fields/Date';

interface IPatientContactItemAddEditProps {
    contact?: IPatientContact;
    episodeCategoryId: string;
    formId: string;
    isEditing: boolean;
    onCancel: (contactId: string | undefined) => void;
    onSave: (contactId: string) => Promise<void>;
    onUpdate: (contact: IPatientContactAddEdit | undefined) => Promise<void>;
    patientId: string;
}

export interface IPatientContactAddEdit {
    date: string;
    episodeCategoryId: string;
    id?: string;
    patientId: string;
    report: string | null;
}

interface IPatientContactItemAddEditState {
    contact?: IPatientContactAddEdit;
    errors: ValidationErrors;
}

export class PatientContactItemAddEdit extends Component<
    IPatientContactItemAddEditProps,
    IPatientContactItemAddEditState
> {
    constructor(props: IPatientContactItemAddEditProps, context?: any) {
        super(props, context);

        this.state = {
            errors: emptyValidationErrors,
        };
    }

    public async componentDidMount() {
        await this.load();
    }

    public render() {
        if (!this.state.contact) {
            return null;
        }

        const practitioner = this.props.contact?.employment?.practitioner;

        return (
            <Card>
                <Card.Transition type="edit" />
                <ProcessItemContainer>
                    <ProcessItemHeader
                        color="secondary"
                        date={this.state.contact.date}
                        icon={ServiceType.contact}
                        organizationName={this.props.contact?.employment?.organization?.name}
                        practitionerName={practitioner ? getDisplayName(practitioner) : undefined}
                        title="Contact"
                    />
                    <Form
                        id="newPatientContact"
                        submitOnEnter={false}
                    >
                        <FormContextProvider
                            value={{
                                placeholderGroupName: 'contact',
                                item: this.state.contact,
                                errors: this.state.errors,
                                onChange: async (key, value) => await this.handleChange(key, value),
                                onDoValidate: async (key) => await this.validateOne(key),
                            }}
                        >
                            <TextAreaFormGroupField
                                error={this.state.errors.get('report')}
                                fieldKey="report"
                                hasErrors={this.state.errors.has('report')}
                                isRequired
                                label={translateKey('common.report')}
                                maxLength={10000}
                                placeholder="Beschrijf wat er is gemeld of afgesproken..."
                            />
                            <FormGroup
                                hasErrors={this.state.errors.has('date')}
                                isRequired
                                label={translateKey('request.dateContact')}
                            >
                                <DateInput
                                    error={this.state.errors.get('date')}
                                    fieldKey="date"
                                    hasMaxWidth={true}
                                    maxDate={DateTime.now().format()}
                                    placeholder={`${translateKey('common.date')}...`}
                                    size="medium"
                                    variant="outline"
                                />
                            </FormGroup>
                        </FormContextProvider>
                        <FormButtonRow>
                            <Button
                                onClick={async () => await this.props.onCancel(this.state.contact?.id)}
                                variant="outline"
                            >
                                {translateKey('common.cancel')}
                            </Button>
                            {this.props.isEditing ? (
                                <Button onClick={async () => await this.updatePatientContact()}>
                                    {translateKey('common.save')}
                                </Button>
                            ) : (
                                <Button onClick={async () => await this.savePatientContact()}>
                                    {translateKey('common.register')}
                                </Button>
                            )}
                        </FormButtonRow>
                    </Form>
                </ProcessItemContainer>
            </Card>
        );
    }

    private async handleChange<T, K extends keyof T>(name: K, value: T[K]) {
        await this.setState(({ contact }) => ({ contact: { ...contact, [name]: value } }));
    }

    private async validateOne(name: string) {
        await this.setState(({ contact, errors }) => ({
            errors: updateValidationErrorsForOne(patientContactSchema, contact, errors, name),
        }));
    }

    private async load() {
        if (this.props.contact) {
            const contact = {
                date: this.props.contact.date,
                episodeCategoryId: this.props.episodeCategoryId,
                id: this.props.contact.id,
                patientId: this.props.patientId,
                report: this.props.contact.report,
            };
            await this.setState({ contact });
        } else {
            await this.setState({
                contact: {
                    date: DateTime.now().format('YYYY-MM-DD'),
                    episodeCategoryId: this.props.episodeCategoryId,
                    patientId: this.props.patientId,
                    report: null,
                },
            });
        }
    }

    private async savePatientContact() {
        if (!this.state.contact) {
            return;
        }
        const url = '/api/outpatientClinic/patientContacts';

        const validatedContact = validate(patientContactSchema, this.state.contact);
        await this.setState({ errors: validatedContact.errors });

        if (validatedContact.errors.size > 0) {
            scrollValidationErrorIntoCenterViewInScrollContainer(this.props.formId, validatedContact.errors);

            return;
        }

        const result = await fetchJson(url, 'POST', { patientContact: validatedContact.item });

        await this.props.onSave(result.json.id);
    }

    private async updatePatientContact() {
        if (!this.state.contact) {
            return;
        }
        const url = `/api/outpatientClinic/patientContacts/${this.state.contact.id}`;

        const validatedContact = validate(patientContactUpdateSchema, this.state.contact);
        await this.setState({ errors: validatedContact.errors });

        if (validatedContact.errors.size > 0) {
            scrollValidationErrorIntoCenterViewInScrollContainer(this.props.formId, validatedContact.errors);

            return;
        }

        const result = await fetchJson(url, 'POST', { patientContact: validatedContact.item });
        await this.props.onUpdate(result.json.id);
    }
}
